import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { Link, NavLink } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { getMySocialHeaderInDb, listSocialConnectionsInDb } from "@/lib/supabase/social";
import { parseBirthDate, computeKarmaNumerology } from "@/lib/karma/numerology";
import { resilientFetch } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";

const PROGRAM_DAY_TRACKING_KEY = "numeroloji_program_day_tracking_v1";
const RITUAL_SERIES_TRACKS = ["meditation", "breath", "nutrition", "ritual"] as const;

type RitualSeriesTrack = (typeof RITUAL_SERIES_TRACKS)[number];

type ProgramSeriesItem = {
  id: string;
  tags: string[];
  iconKey?: string;
};

type ProgramDayTrackingState = {
  startedAtByItemId: Record<string, string>;
};

type ProgramDayTrackingStore = Record<string, ProgramDayTrackingState>;

function initials(name: string) {
  return String(name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toLocaleUpperCase("tr-TR") ?? "")
    .join("") || "?";
}

function normalizeTag(value: string) {
  return String(value ?? "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z:_-]+/g, "");
}

function formatLocalDateKey(d: Date) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseProgramDaysFromTags(tags: string[]) {
  const raw = (tags ?? []).find((tag) => String(tag ?? "").startsWith("program_gun:"))?.slice("program_gun:".length) ?? "";
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

function parseRitualProgramDaysFromTags(tags: string[]) {
  const raw = (tags ?? []).find((tag) => String(tag ?? "").startsWith("rituel_program_gun:"))?.slice("rituel_program_gun:".length) ?? "";
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

async function fetchSeriesItems(path: string): Promise<ProgramSeriesItem[]> {
  try {
    const res = await resilientFetch(path);
    const json = (await res.json().catch(() => null)) as { items?: ProgramSeriesItem[] } | null;
    if (!res.ok) return [];
    return Array.isArray(json?.items) ? json.items : [];
  } catch {
    return [];
  }
}

async function fetchMeditationSeriesItems(): Promise<ProgramSeriesItem[]> {
  return fetchSeriesItems("/api/numeroloji/meditations");
}

async function fetchDietSeriesItems(): Promise<ProgramSeriesItem[]> {
  return fetchSeriesItems("/api/numeroloji/diets");
}

async function fetchActivitySeriesItems(): Promise<ProgramSeriesItem[]> {
  return fetchSeriesItems("/api/numeroloji/activities");
}

export function ProfileOverviewHeader() {
  const { user, loading } = useSupabaseSession();

  const headerQuery = useQuery({
    queryKey: ["social-hub-header", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: () => getMySocialHeaderInDb(),
    staleTime: 30_000,
  });

  const connectionsQuery = useQuery({
    queryKey: ["social-hub-connections", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: () => listSocialConnectionsInDb(),
    staleTime: 10_000,
  });

  const meditationSeriesQuery = useQuery({
    queryKey: ["ritual-series-meditations", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchMeditationSeriesItems,
    staleTime: 60_000,
  });

  const dietSeriesQuery = useQuery({
    queryKey: ["ritual-series-diets", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchDietSeriesItems,
    staleTime: 60_000,
  });

  const activitySeriesQuery = useQuery({
    queryKey: ["ritual-series-activities", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchActivitySeriesItems,
    staleTime: 60_000,
  });

  const ritualSeriesStatus = useMemo<Record<RitualSeriesTrack, boolean>>(() => {
    if (!user?.id || typeof window === "undefined") {
      return { meditation: false, breath: false, nutrition: false, ritual: false };
    }

    const todayKey = formatLocalDateKey(new Date());
    const userKey = `sb:${user.id}`;

    let startedAtByItemId: Record<string, string> = {};
    try {
      const raw = window.localStorage.getItem(PROGRAM_DAY_TRACKING_KEY);
      const parsed = raw ? (JSON.parse(raw) as ProgramDayTrackingStore) : null;
      startedAtByItemId = parsed?.[userKey]?.startedAtByItemId ?? {};
    } catch {
      startedAtByItemId = {};
    }

    const resolveProgramDay = (startedDateKey?: string) => {
      if (!startedDateKey) return 0;
      const started = new Date(`${startedDateKey}T00:00:00`);
      const today = new Date(`${todayKey}T00:00:00`);
      if (!Number.isFinite(started.getTime()) || !Number.isFinite(today.getTime())) return 0;
      const diffDays = Math.floor((today.getTime() - started.getTime()) / 86_400_000);
      return diffDays >= 0 ? diffDays + 1 : 0;
    };

    const hasCompletedSeries = (items: ProgramSeriesItem[], getProgramDays: (tags: string[]) => number, predicate?: (item: ProgramSeriesItem) => boolean) =>
      items.some((item) => {
        if (predicate && !predicate(item)) return false;
        const programDays = getProgramDays(item.tags ?? []);
        if (programDays <= 0) return false;
        return resolveProgramDay(startedAtByItemId[item.id]) >= programDays;
      });

    const meditations = meditationSeriesQuery.data ?? [];
    const diets = dietSeriesQuery.data ?? [];
    const activities = activitySeriesQuery.data ?? [];

    const isBreath = (item: ProgramSeriesItem) => {
      const tags = new Set((item.tags ?? []).map((tag) => normalizeTag(tag)));
      return tags.has("nefes") || tags.has("breath");
    };

    const isMeditationOnly = (item: ProgramSeriesItem) => {
      const tags = new Set((item.tags ?? []).map((tag) => normalizeTag(tag)));
      if (tags.has("nefes") || tags.has("breath")) return false;
      return true;
    };

    const isRitual = (item: ProgramSeriesItem) => {
      const icon = normalizeTag(item.iconKey ?? "");
      const tags = new Set((item.tags ?? []).map((tag) => normalizeTag(tag)));
      return icon === "rituel" || tags.has("rituel") || tags.has("ritual") || (item.tags ?? []).some((tag) => String(tag ?? "").startsWith("rituel_program_gun:"));
    };

    return {
      meditation: hasCompletedSeries(meditations, parseProgramDaysFromTags, isMeditationOnly),
      breath: hasCompletedSeries(meditations, parseProgramDaysFromTags, isBreath),
      nutrition: hasCompletedSeries(diets, parseProgramDaysFromTags),
      ritual: hasCompletedSeries(activities, parseRitualProgramDaysFromTags, isRitual),
    };
  }, [activitySeriesQuery.data, dietSeriesQuery.data, meditationSeriesQuery.data, user?.id]);

  const completedSeriesCount = RITUAL_SERIES_TRACKS.filter((track) => ritualSeriesStatus[track]).length;
  const connections = connectionsQuery.data;
  const energyScore = Math.min(99, 68 + (connections?.friendCount ?? 0));
  const spiritualBondCount = (connections?.followerCount ?? 0) + (connections?.followingCount ?? 0);

  const quickLinks = [
    { to: "/panel/meditasyon", label: "Meditasyon" },
    { to: "/panel/nefes", label: "Nefes" },
    { to: "/panel/diyet", label: "Diyet" },
    { to: "/panel/aktivite", label: "Aktivite" },
    { to: "/panel/rituel", label: "Ritüel" },
  ];

  useEffect(() => {
    if (!user?.id || loading) return;
    void headerQuery.refetch();
  }, [headerQuery, loading, user?.id]);

  const fallbackHeader = useMemo(() => {
    const meta = (user?.metadata ?? {}) as Record<string, unknown>;
    const first = typeof meta.first_name === "string" ? meta.first_name.trim() : typeof meta.firstName === "string" ? meta.firstName.trim() : "";
    const last = typeof meta.last_name === "string" ? meta.last_name.trim() : typeof meta.lastName === "string" ? meta.lastName.trim() : "";
    const full = `${first} ${last}`.trim();
    const fullName = typeof meta.full_name === "string" ? meta.full_name.trim() : typeof meta.fullName === "string" ? meta.fullName.trim() : "";
    const displayName = typeof meta.display_name === "string" ? meta.display_name.trim() : typeof meta.displayName === "string" ? meta.displayName.trim() : "";
    const handle =
      (typeof meta.handle === "string" ? meta.handle.trim() : "") ||
      (typeof user?.email === "string" && user.email.includes("@") ? user.email.split("@")[0].trim() : "") ||
      "user";

    const birthDay = Number(meta.birth_day ?? meta.birthDay ?? 0);
    const birthMonth = Number(meta.birth_month ?? meta.birthMonth ?? 0);
    const birthYear = Number(meta.birth_year ?? meta.birthYear ?? 0);

    let destinyNumber: number | null = null;
    let lifePathNumber: number | null = null;
    let expressionNumber: number | null = null;

    if (birthDay > 0 && birthMonth > 0 && birthYear > 0) {
      try {
        const birthDate = parseBirthDate({ day: birthDay, month: birthMonth, year: birthYear });
        const numerology = computeKarmaNumerology(full || fullName || displayName || handle || "Kullanıcı", birthDate);
        destinyNumber = numerology.destinyNumber;
        lifePathNumber = numerology.lifePathMaster ?? numerology.destinyNumber;
        expressionNumber = numerology.expressionNumber;
      } catch {
        destinyNumber = null;
        lifePathNumber = null;
        expressionNumber = null;
      }
    }

    return {
      id: String(user?.id ?? ""),
      displayName: full || fullName || displayName || "Profil",
      handle,
      photoUrl: null,
      bio: null,
      interests: [] as string[],
      matchEnabled: true,
      destinyNumber,
      lifePathNumber,
      expressionNumber,
    };
  }, [user?.email, user?.id, user?.metadata]);

  const header = {
    ...fallbackHeader,
    ...(headerQuery.data ?? {}),
    lifePathNumber: headerQuery.data?.lifePathNumber ?? fallbackHeader.lifePathNumber,
    expressionNumber: headerQuery.data?.expressionNumber ?? fallbackHeader.expressionNumber,
  };

  return (
    <section className="rounded-[30px] border border-[#d7b6d8] bg-gradient-to-br from-[#fff7fc] via-[#f7e9f7] to-[#f2e0f1] p-5 shadow-sm">
      <div className="mb-5 flex justify-end">
        <div className="flex flex-wrap items-center justify-end gap-2">
          {quickLinks.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "rounded-xl border px-3 py-2 text-xs font-semibold transition-colors sm:text-sm",
                  isActive
                    ? "border-[#8f78e5] bg-[#8f78e5] text-white"
                    : "border-[#d8bad7] bg-[#f8edf7] text-[#3a2745] hover:bg-[#f1e5f1]",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-[1fr_auto]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
            <AvatarImage src={header?.photoUrl ?? ""} alt={header?.displayName ?? "Profil"} />
            <AvatarFallback className="text-xl font-bold">{initials(header?.displayName ?? "Kullanıcı")}</AvatarFallback>
          </Avatar>

          <div className="min-w-0">
            <h1 className="truncate text-3xl font-black tracking-tight text-[#2f1f3c]">{header?.displayName ?? "Profil"}</h1>
            <p className="mt-1 text-sm font-medium text-[#6f5a77]">@{header?.handle ?? "user"}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <Badge className="bg-primary/10 text-primary hover:bg-primary/10">Yaşam Yolu: {header?.lifePathNumber ?? "-"}</Badge>
              <Badge variant="secondary">İfade Numarası: {header?.expressionNumber ?? "-"}</Badge>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button type="button" className="rounded-xl">Mesaj Gönder</Button>
              <Button type="button" variant="outline" className="rounded-xl">
                Takip Et
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 rounded-2xl border border-[#d8bad7] bg-white/95 p-4 text-center">
          <div>
            <div className="text-2xl font-black text-primary">{energyScore}%</div>
            <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Enerji Seviyesi</div>
          </div>
          <Link to="/panel/profil/rituel-serisi" className="rounded-xl px-1 py-1 transition-colors hover:bg-[#f6edf8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
            <div className="text-2xl font-black text-primary">{completedSeriesCount}</div>
            <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Seri Özeti</div>
          </Link>
          <div>
            <div className="text-2xl font-black text-primary">{spiritualBondCount}</div>
            <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Ruhsal Bağ</div>
          </div>
        </div>
      </div>
    </section>
  );
}
